import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DoorOpen,
  ScanLine,
  Ticket,
  UserRound,
  XCircle,
} from "lucide-react";

const DEFAULT_GATES = ["Main Door"];

const statusTone = {
  VALID: "border-green-500 bg-green-500/10 text-green-300",
  ALREADY_SCANNED: "border-yellow-500 bg-yellow-500/10 text-yellow-300",
  INVALID: "border-red-500 bg-red-500/10 text-red-300",
  PIN_ERROR: "border-red-500 bg-red-500/10 text-red-300",
  WRONG_EVENT: "border-red-500 bg-red-500/10 text-red-300",
};

function normalizeScannerInput(value) {
  const raw = (value || "").trim();
  if (!raw) return "";
  try {
    const decoded = decodeURIComponent(raw);
    const url = new URL(decoded);
    const fromQuery = ["ticket_id", "ticket", "code", "booking_id", "booking"]
      .map((key) => url.searchParams.get(key))
      .find(Boolean);
    if (fromQuery) return fromQuery.trim();
  } catch {
    // Plain QR payloads are expected; non-URL input continues below.
  }
  const decoded = (() => {
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  })();
  const match = decoded.match(/\b(?:tix|bk|book)_[A-Za-z0-9]+\b/);
  return match ? match[0] : decoded;
}

export default function Scanner() {
  const [pin, setPin] = useState("");
  const [eventCode, setEventCode] = useState("");
  const [gate, setGate] = useState(DEFAULT_GATES[0]);
  const [gates, setGates] = useState(DEFAULT_GATES);
  const [scannerEvent, setScannerEvent] = useState(null);
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const entryPoint = useMemo(() => gate, [gate]);

  const canStart = eventCode.trim() && entryPoint && /^\d{4,8}$/.test(pin);

  const fetchDashboard = useCallback(async () => {
    if (!authed || !eventCode.trim() || !pin) return;
    const { data } = await api.post("/scanner/live-dashboard", {
      event_code: eventCode.trim().toUpperCase(),
      pin,
    });
    setDashboard(data);
    setLastUpdated(new Date());
  }, [authed, eventCode, pin]);

  useEffect(() => {
    if (!authed) return undefined;
    fetchDashboard().catch(() => {});
    const timer = setInterval(() => fetchDashboard().catch(() => {}), 5000);
    return () => clearInterval(timer);
  }, [authed, fetchDashboard]);

  const verify = async () => {
    if (!canStart) return;
    setVerifying(true);
    setResult(null);
    try {
      const { data } = await api.post("/scanner/verify-pin", {
        pin,
        event_code: eventCode.trim().toUpperCase(),
      });
      setScannerEvent(data);
      const configuredGates = Array.isArray(data?.scanner_gates) && data.scanner_gates.length
        ? data.scanner_gates
        : DEFAULT_GATES;
      setGates(configuredGates);
      setGate(configuredGates[0]);
      setAuthed(true);
    } catch {
      setResult({ status: "PIN_ERROR", message: "PIN or event code rejected." });
    } finally {
      setVerifying(false);
    }
  };

  const validate = async () => {
    const scanCode = normalizeScannerInput(code);
    if (!scanCode) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scanner/validate", {
        code: scanCode,
        pin,
        event_code: eventCode.trim().toUpperCase(),
        entry_point: entryPoint,
      });
      setResult(data);
      setCode("");
      fetchDashboard().catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.type === "wrong_event") {
        setResult({
          status: "WRONG_EVENT",
          message: "Wrong event ticket.",
          detail,
        });
      } else {
        setResult({ status: "PIN_ERROR", message: detail || "Ticket could not be validated." });
      }
    } finally {
      setBusy(false);
    }
  };

  const resetSession = () => {
    setAuthed(false);
    setPin("");
    setCode("");
    setResult(null);
    setDashboard(null);
    setScannerEvent(null);
  };

  return (
    <div className="min-h-screen bg-[#06070a] text-white">
      <Navbar />
      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] font-black text-primary">Door Scanner</p>
          <h1 className="font-display text-4xl font-black mt-2 flex items-center gap-3">
            <ScanLine className="h-8 w-8 text-primary" />
            Live gate entry
          </h1>
        </div>

        {!authed ? (
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
            <section className="rounded-2xl border border-white/15 bg-black/50 p-6 relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 scanner-line" />
              <h2 className="font-display text-2xl font-black">Start scanner session</h2>
              <div className="mt-5 grid md:grid-cols-2 gap-3">
                <Field label="Event Code">
                  <Input data-testid="event-code-input" placeholder="CA8E08" value={eventCode} onChange={(e) => setEventCode(e.target.value.toUpperCase())} className="bg-black/40 border-white/15 text-white font-mono tracking-[0.2em]" />
                </Field>
                <Field label="PIN">
                  <Input data-testid="scanner-pin-input" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} placeholder="2468" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} className="bg-black/40 border-white/15 text-white" />
                </Field>
              </div>
              <Button onClick={verify} disabled={!canStart || verifying} className="mt-5 w-full rounded-full" data-testid="scanner-pin-submit">
                {verifying ? "Checking…" : "Access Scanner"}
              </Button>
              {result?.status === "PIN_ERROR" && (
                <p className="mt-3 text-sm text-red-300">{result.message}</p>
              )}
            </section>

            <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <h3 className="font-display text-xl font-black">How gate entry works</h3>
              <div className="mt-4 space-y-3 text-sm text-white/70">
                <Info icon={DoorOpen} text="Choose one of the organiser's event gates, then enter the Event Code and PIN." />
                <Info icon={Ticket} text="Scan or paste the individual tix_ QR code shown on the customer ticket." />
                <Info icon={Clock} text="Each accepted scan records the exact time and entry point." />
                <Info icon={Activity} text="The dashboard refreshes every 5 seconds for all doors." />
              </div>
            </section>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6">
            <section className="rounded-2xl border border-white/15 bg-black/50 p-6 relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 scanner-line" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/50 font-black">Scanning For</p>
                  <h2 className="font-display text-2xl font-black mt-1">{scannerEvent?.event_title || dashboard?.event?.title || eventCode}</h2>
                  <p className="text-sm text-white/60 mt-1">{entryPoint}</p>
                  <Select value={gate} onValueChange={setGate}>
                    <SelectTrigger data-testid="scanner-gate-select" className="mt-3 w-full bg-black/40 border-white/15 text-white">
                      <SelectValue placeholder="Choose gate" />
                    </SelectTrigger>
                    <SelectContent>
                      {gates.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={resetSession} className="rounded-full bg-transparent border-white/25 text-white hover:bg-white/10">Change</Button>
              </div>

              <div className="mt-6 space-y-3">
                <Input
                  data-testid="ticket-code-input"
                  placeholder="Paste ticket QR code e.g. tix_xxxxxx"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") validate(); }}
                  className="bg-black/40 border-white/15 text-white font-mono"
                />
                <Button onClick={validate} disabled={busy || !code.trim()} className="w-full rounded-full" data-testid="ticket-validate-btn">
                  {busy ? "Validating…" : "Validate Ticket"}
                </Button>
              </div>

              {result && (
                <ScanResult result={result} />
              )}
            </section>

            <LiveDashboard dashboard={dashboard} lastUpdated={lastUpdated} />
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-1.5">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/45">{label}</span>
      {children}
    </label>
  );
}

function Info({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <p>{text}</p>
    </div>
  );
}

function ScanResult({ result }) {
  const status = result.status || "INVALID";
  const booking = result.booking || {};
  const ticket = result.ticket || {};
  const tone = statusTone[status] || statusTone.INVALID;
  const Icon = status === "VALID" ? CheckCircle2 : status === "ALREADY_SCANNED" ? AlertTriangle : XCircle;
  const title =
    status === "VALID" ? (result.message || "Ticket checked in") :
    status === "ALREADY_SCANNED" ? (result.message || "Already checked in") :
    status === "WRONG_EVENT" ? "Wrong event ticket" :
    "Ticket rejected";
  return (
    <div className={`mt-5 p-5 rounded-2xl border-2 ${tone}`} data-testid="scan-result">
      <div className="flex items-start gap-3">
        <Icon className="h-7 w-7 shrink-0" />
        <div className="min-w-0">
          <p className="font-display text-xl font-black">{title}</p>
          {booking.user_name || booking.user_email ? (
            <p className="text-sm text-white/85">{booking.user_name} · {booking.user_email}</p>
          ) : null}
          {booking.item_title ? <p className="text-xs text-white/65">{booking.item_title}</p> : null}
          {ticket.ticket_number ? <p className="text-xs text-white/65">Individual ticket #{ticket.ticket_number}</p> : null}
          {result.checked_in_at ? <p className="text-xs text-white/50">Time: {new Date(result.checked_in_at).toLocaleString()}</p> : null}
          {result.detail?.ticket_title ? <p className="text-xs text-white/65">Ticket is for: {result.detail.ticket_title}</p> : null}
          {result.detail?.scanned_event ? <p className="text-xs text-white/65">This door: {result.detail.scanned_event}</p> : null}
        </div>
      </div>
    </div>
  );
}

function LiveDashboard({ dashboard, lastUpdated }) {
  const summary = dashboard?.summary || {};
  const checkins = dashboard?.checkins || [];
  return (
    <section className="rounded-2xl border border-white/15 bg-white/[0.04] p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-black">Live Dashboard</p>
          <h3 className="font-display text-2xl font-black mt-1">Gate activity</h3>
        </div>
        <p className="text-xs text-white/45">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Waiting"}</p>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5">
        <Metric label="Checked In" value={summary.checked_in_tickets ?? 0} />
        <Metric label="Remaining" value={summary.remaining_tickets ?? 0} />
        <Metric label="Rate" value={`${summary.check_in_rate ?? 0}%`} />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-5">
        <Breakdown title="By Gate" rows={dashboard?.by_entry || []} labelKey="entry_point" />
      </div>

      <div className="mt-5">
        <p className="text-xs uppercase tracking-[0.25em] text-white/45 font-black">Recent Scans</p>
        <div className="mt-3 space-y-2">
          {checkins.length === 0 ? (
            <p className="text-sm text-white/50">No scans yet.</p>
          ) : checkins.slice(0, 8).map((row) => (
            <div key={row.checkin_id || row.id} className="rounded-xl border border-white/10 bg-black/30 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold truncate">{row.user_name || row.user_email || "Guest"}</p>
                <p className="text-xs text-white/45 truncate">{row.entry_point || "Gate"} · {row.category_name || row.category || "Ticket"}</p>
              </div>
              <p className="text-xs text-white/50 shrink-0">{row.scanned_at ? new Date(row.scanned_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/35 p-4">
      <p className="font-display text-2xl font-black text-primary">{value}</p>
      <p className="text-xs text-white/50 mt-1">{label}</p>
    </div>
  );
}

function Breakdown({ title, rows, labelKey }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
      <p className="text-sm font-black">{title}</p>
      <div className="mt-3 space-y-2">
        {rows.length === 0 ? (
          <p className="text-xs text-white/45">No data yet.</p>
        ) : rows.slice(0, 5).map((row) => (
          <div key={row[labelKey]} className="flex justify-between gap-3 text-xs">
            <span className="text-white/60 truncate">{row[labelKey]}</span>
            <span className="font-bold text-white">{row.tickets}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
